import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ArrowLeft, Download, TrendingUp, AlertTriangle, Users, Calendar, Target, Trophy, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import HeaderNav from '@/components/HeaderNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

function Reports() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Report data states
  const [activityReport, setActivityReport] = useState([]);
  const [backlogReport, setBacklogReport] = useState([]);
  const [deadLeadReport, setDeadLeadReport] = useState([]);
  const [sourceReport, setSourceReport] = useState([]);
  const [projectReport, setProjectReport] = useState([]);
  const [channelPartnerReport, setChannelPartnerReport] = useState([]); // Channel Partner Performance
  const [siteVisitsReport, setSiteVisitsReport] = useState([]); // Site Visits Report
  const [bookingsReport, setBookingsReport] = useState(null); // FEATURE 3: Bookings Report
  
  // Expandable rows state for breakdowns
  const [expandedSources, setExpandedSources] = useState({}); // FEATURE 1
  const [expandedProjects, setExpandedProjects] = useState({}); // FEATURE 2

  // Check user role
  const isSuperAdmin = user?.user_role === 'Super Administrator';

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    const params = {};
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;

    try {
      // Load reports sequentially for better reliability
      const responses = [];
      
      try {
        const activityRes = await axios.get(`${API}/reports/activity-by-brokers`, { 
          headers: { Authorization: `Bearer ${token}` },
          params,
          timeout: 15000
        });
        responses.push(activityRes);
      } catch (err) {
        console.error('Activity report failed:', err);
        responses.push({ data: [] });
      }
      
      try {
        const backlogRes = await axios.get(`${API}/reports/backlog-activities`, { 
          headers: { Authorization: `Bearer ${token}` },
          params,
          timeout: 15000 
        });
        responses.push(backlogRes);
      } catch (err) {
        console.error('Backlog report failed:', err);
        responses.push({ data: [] });
      }
      
      try {
        const deadLeadRes = await axios.get(`${API}/reports/dead-lead-analysis`, { 
          headers: { Authorization: `Bearer ${token}` },
          params,
          timeout: 15000
        });
        responses.push(deadLeadRes);
      } catch (err) {
        console.error('Dead lead report failed:', err);
        responses.push({ data: [] });
      }
      
      try {
        const sourceRes = await axios.get(`${API}/reports/source-effectiveness`, { 
          headers: { Authorization: `Bearer ${token}` },
          params,
          timeout: 15000
        });
        responses.push(sourceRes);
      } catch (err) {
        console.error('Source report failed:', err);
        responses.push({ data: [] });
      }
      
      try {
        const projectRes = await axios.get(`${API}/reports/project-performance`, { 
          headers: { Authorization: `Bearer ${token}` },
          params,
          timeout: 15000
        });
        responses.push(projectRes);
      } catch (err) {
        console.error('Project report failed:', err);
        responses.push({ data: [] });
      }

      // Channel Partner Performance (Super Admin only)
      let channelPartnerRes = { data: [] };
      if (isSuperAdmin) {
        try {
          channelPartnerRes = await axios.get(`${API}/reports/channel-partner-performance`, { 
            headers: { Authorization: `Bearer ${token}` },
            params,
            timeout: 15000
          });
        } catch (err) {
          console.error('Channel partner report failed:', err);
        }
      }

      // Site Visits Report
      let siteVisitsRes = { data: [] };
      try {
        siteVisitsRes = await axios.get(`${API}/reports/site-visits`, { 
          headers: { Authorization: `Bearer ${token}` },
          params,
          timeout: 15000
        });
      } catch (err) {
        console.error('Site visits report failed:', err);
      }

      // FEATURE 3: Bookings Report
      let bookingsRes = { data: null };
      try {
        bookingsRes = await axios.get(`${API}/reports/bookings`, { 
          headers: { Authorization: `Bearer ${token}` },
          params,
          timeout: 15000
        });
      } catch (err) {
        console.error('Bookings report failed:', err);
      }

      const [activityRes, backlogRes, deadLeadRes, sourceRes, projectRes] = responses;
      
      // Set data even if some requests failed
      setActivityReport(activityRes.data || []);
      setBacklogReport(backlogRes.data || []);
      setDeadLeadReport(deadLeadRes.data || []);
      setSourceReport(sourceRes.data || []);
      setProjectReport(projectRes.data || []);
      setChannelPartnerReport(channelPartnerRes.data || []);
      setSiteVisitsReport(siteVisitsRes.data || []);
      setBookingsReport(bookingsRes.data || null); // FEATURE 3
      
    } catch (error) {
      console.error('Critical reports error:', error);
      toast.error('Some reports failed to load - check network connection');
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    // Convert data to CSV format
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => 
      Object.values(row).map(value => 
        typeof value === 'string' && value.includes(',') ? `"${value}"` : value
      ).join(',')
    ).join('\n');
    
    const csvContent = `${headers}\n${rows}`;
    
    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <HeaderNav />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600 mx-auto mb-2"></div>
            <p className="text-slate-600">Loading reports...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reports-page">
      <HeaderNav />
      
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <Button 
              onClick={() => navigate('/')}
              variant="ghost" 
              className="p-2"
              data-testid="back-button"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-bold text-slate-800">Reports & Analytics</h1>
          </div>
          
          {/* Date Range Filters */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Label htmlFor="start-date" className="text-sm font-medium">From:</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                data-testid="start-date-input"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Label htmlFor="end-date" className="text-sm font-medium">To:</Label>
              <Input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                data-testid="end-date-input"
              />
            </div>
            <Button 
              onClick={loadReports}
              className="bg-cyan-600 hover:bg-cyan-700"
              data-testid="filter-button"
            >
              Apply Filter
            </Button>
          </div>
        </div>

        <Tabs defaultValue="activity" className="space-y-6">
          <TabsList className="grid w-full grid-cols-7">
            <TabsTrigger value="activity" data-testid="activity-tab">Activity</TabsTrigger>
            <TabsTrigger value="backlog" data-testid="backlog-tab">Active Leads</TabsTrigger>
            <TabsTrigger value="source" data-testid="source-tab">Source</TabsTrigger>
            <TabsTrigger value="dead-leads" data-testid="dead-leads-tab">Dead Leads</TabsTrigger>
            <TabsTrigger value="project" data-testid="project-tab">Project</TabsTrigger>
            <TabsTrigger value="site-visits" data-testid="site-visits-tab">Site Visits</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="bookings-tab">Bookings</TabsTrigger>
          </TabsList>

          {/* Activity Report */}
          <TabsContent value="activity">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Summary Cards */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Edits</p>
                      <p className="text-2xl font-bold">
                        {activityReport.reduce((sum, row) => sum + (row.total_edits || 0), 0)}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-cyan-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Calls</p>
                      <p className="text-2xl font-bold">
                        {activityReport.reduce((sum, row) => sum + (row.total_calls || 0), 0)}
                      </p>
                    </div>
                    <Calendar className="h-10 w-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Site Visits</p>
                      <p className="text-2xl font-bold">
                        {activityReport.reduce((sum, row) => sum + (row.site_visits || 0), 0)}
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-purple-600" />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Status Changes</p>
                      <p className="text-2xl font-bold">
                        {activityReport.reduce((sum, row) => sum + (row.status_edits || 0), 0)}
                      </p>
                    </div>
                    <Target className="h-10 w-10 text-green-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Activity Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Activity Overview</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={activityReport}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="user" 
                        fontSize={12}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_calls" fill="#3b82f6" name="Total Calls" />
                      <Bar dataKey="site_visits" fill="#8b5cf6" name="Site Visits" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Activity Table */}
              <Card data-testid="activity-report-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Activity by Sales Rep</CardTitle>
                    <Button
                      variant="outline"
                      onClick={() => exportToCSV(activityReport, 'activity_report')}
                      data-testid="export-activity-button"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Sales Rep</TableHead>
                          <TableHead className="text-right">Total Edits</TableHead>
                          <TableHead className="text-right">Status Changes</TableHead>
                          <TableHead className="text-right">Total Calls</TableHead>
                          <TableHead className="text-right">Site Visits</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activityReport.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{row.user}</TableCell>
                            <TableCell className="text-right">{row.total_edits}</TableCell>
                            <TableCell className="text-right">{row.status_edits}</TableCell>
                            <TableCell className="text-right">{row.total_calls}</TableCell>
                            <TableCell className="text-right">{row.site_visits}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Backlog Activities Report */}
          <TabsContent value="backlog">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Summary Cards */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Active Leads</p>
                      <p className="text-2xl font-bold">
                        {backlogReport.reduce((sum, row) => sum + (row.total || 0), 0)}
                      </p>
                    </div>
                    <AlertTriangle className="h-10 w-10 text-amber-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">New Leads</p>
                      <p className="text-2xl font-bold">
                        {backlogReport.reduce((sum, row) => sum + (row.new || 0), 0)}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Follow ups</p>
                      <p className="text-2xl font-bold">
                        {backlogReport.reduce((sum, row) => sum + (row.follow_up || 0), 0)}
                      </p>
                    </div>
                    <Calendar className="h-10 w-10 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Site Visits Planned</p>
                      <p className="text-2xl font-bold">
                        {backlogReport.reduce((sum, row) => sum + (row.site_visit_planned || 0) + (row.site_visit_done || 0), 0)}
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-green-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Backlog Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Active Leads Distribution by Sales Rep</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={backlogReport}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="user" fontSize={10} />
                    <YAxis fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="new" fill="#3b82f6" name="New" />
                    <Bar dataKey="follow_up" fill="#f59e0b" name="Follow Up" />
                    <Bar dataKey="site_visit_planned" fill="#10b981" name="Site Visit Planned" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Backlog Table */}
            <Card data-testid="backlog-report-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Active Leads by Sales Rep</CardTitle>
                  <Button
                    variant="outline"
                    onClick={() => exportToCSV(backlogReport, 'backlog_activities')}
                    data-testid="export-backlog-button"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Sales Rep</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">New</TableHead>
                        <TableHead className="text-right">Attempted 1</TableHead>
                        <TableHead className="text-right">Attempted 2</TableHead>
                        <TableHead className="text-right">Attempted 3</TableHead>
                        <TableHead className="text-right">Follow Up</TableHead>
                        <TableHead className="text-right">Site Visit Planned</TableHead>
                        <TableHead className="text-right">Site Visit Done</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {backlogReport.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{row.user}</TableCell>
                          <TableCell className="text-right">{row.total}</TableCell>
                          <TableCell className="text-right">{row.new}</TableCell>
                          <TableCell className="text-right">{row.attempted_1}</TableCell>
                          <TableCell className="text-right">{row.attempted_2}</TableCell>
                          <TableCell className="text-right">{row.attempted_3}</TableCell>
                          <TableCell className="text-right">{row.follow_up}</TableCell>
                          <TableCell className="text-right">{row.site_visit_planned}</TableCell>
                          <TableCell className="text-right">{row.site_visit_done}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Source Effectiveness Report */}
          <TabsContent value="source">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Summary Cards */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Sources</p>
                      <p className="text-2xl font-bold">{sourceReport.length}</p>
                    </div>
                    <Target className="h-10 w-10 text-cyan-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Leads</p>
                      <p className="text-2xl font-bold">
                        {sourceReport.reduce((sum, row) => sum + (row.total_leads || 0), 0)}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Booked Leads</p>
                      <p className="text-2xl font-bold">
                        {sourceReport.reduce((sum, row) => sum + (row.booked || 0), 0)}
                      </p>
                    </div>
                    <Trophy className="h-10 w-10 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Avg Conversion</p>
                      <p className="text-2xl font-bold">
                        {sourceReport.length > 0 
                          ? (sourceReport.reduce((sum, row) => sum + (row.conversion_rate || 0), 0) / sourceReport.length).toFixed(1) 
                          : 0}%
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Source Chart */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <Card>
                <CardHeader>
                  <CardTitle>Leads by Source</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={sourceReport}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="source" 
                        fontSize={10}
                        tick={{ fontSize: 10 }}
                        interval={0}
                        angle={-45}
                        textAnchor="end"
                        height={70}
                      />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="total_leads" fill="#06b6d4" name="Total Leads" />
                      <Bar dataKey="booked" fill="#10b981" name="Booked" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top performing sources */}
              <Card>
                <CardHeader>
                  <CardTitle>Top Performing Sources</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {[...sourceReport]
                      .sort((a, b) => (b.conversion_rate || 0) - (a.conversion_rate || 0))
                      .slice(0, 5)
                      .map((source, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium">{source.source}</p>
                            <p className="text-sm text-slate-600">{source.total_leads} leads</p>
                          </div>
                          <Badge variant={source.conversion_rate >= 10 ? "default" : source.conversion_rate >= 5 ? "secondary" : "outline"}>
                            {source.conversion_rate.toFixed(1)}%
                          </Badge>
                        </div>
                      ))
                    }
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Source Effectiveness Table with Project Breakdown */}
            <Card data-testid="source-report-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Source Effectiveness</CardTitle>
                  <Button
                    variant="outline"
                    onClick={() => exportToCSV(sourceReport, 'source_effectiveness')}
                    data-testid="export-source-button"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead className="text-right">Total Leads</TableHead>
                        <TableHead className="text-right">Booked</TableHead>
                        <TableHead className="text-right">Conversion Rate</TableHead>
                        <TableHead className="text-right">Active Leads</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sourceReport.map((row, idx) => (
                        <React.Fragment key={idx}>
                          <TableRow 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => setExpandedSources(prev => ({...prev, [idx]: !prev[idx]}))}
                          >
                            <TableCell>
                              {row.project_breakdown?.length > 0 && (
                                <span className="text-slate-400">{expandedSources[idx] ? '▼' : '▶'}</span>
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{row.source}</TableCell>
                            <TableCell className="text-right">{row.total_leads}</TableCell>
                            <TableCell className="text-right">{row.booked}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={row.conversion_rate >= 10 ? "default" : row.conversion_rate >= 5 ? "secondary" : "outline"}>
                                {row.conversion_rate.toFixed(1)}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{row.active_leads}</TableCell>
                          </TableRow>
                          {/* FEATURE 1: Project Breakdown Rows */}
                          {expandedSources[idx] && row.project_breakdown?.map((proj, pIdx) => (
                            <TableRow key={`${idx}-${pIdx}`} className="bg-slate-50">
                              <TableCell></TableCell>
                              <TableCell className="pl-8 text-slate-600">
                                └─ {proj.project}
                              </TableCell>
                              <TableCell className="text-right text-slate-600">{proj.total_leads}</TableCell>
                              <TableCell className="text-right text-slate-600">{proj.booked}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className="text-xs">
                                  {proj.conversion_rate.toFixed(1)}%
                                </Badge>
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Channel Partner Performance Table (Super Admin Only) */}
            {isSuperAdmin && channelPartnerReport.length > 0 && (
              <Card className="mt-6">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Channel Partner Performance</CardTitle>
                    <Button
                      variant="outline"
                      onClick={() => exportToCSV(channelPartnerReport, 'channel_partner_performance')}
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Channel Partner</TableHead>
                          <TableHead className="text-right">Total Leads</TableHead>
                          <TableHead className="text-right">Site Visits Planned</TableHead>
                          <TableHead className="text-right">Site Visits Done</TableHead>
                          <TableHead className="text-right">Booked</TableHead>
                          <TableHead className="text-right">Conversion Rate</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {channelPartnerReport.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{row.channel_partner}</TableCell>
                            <TableCell className="text-right">{row.total_leads}</TableCell>
                            <TableCell className="text-right">{row.site_visits_planned}</TableCell>
                            <TableCell className="text-right">{row.site_visits_done}</TableCell>
                            <TableCell className="text-right">{row.booked}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={row.conversion_rate >= 10 ? "default" : row.conversion_rate >= 5 ? "secondary" : "outline"}>
                                {row.conversion_rate.toFixed(1)}%
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Dead Leads Analysis */}
          <TabsContent value="dead-leads">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Dead Leads by Reason</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={deadLeadReport}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="reason" fontSize={10} interval={0} angle={-45} textAnchor="end" height={70} />
                      <YAxis fontSize={12} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#ef4444" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card data-testid="dead-leads-report-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Dead Lead Analysis</CardTitle>
                    <Button
                      variant="outline"
                      onClick={() => exportToCSV(deadLeadReport, 'dead_lead_analysis')}
                      data-testid="export-dead-leads-button"
                    >
                      <Download className="mr-2 h-4 w-4" />
                      Export
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">Percentage</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deadLeadReport.map((row, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="font-medium">{row.reason}</TableCell>
                            <TableCell className="text-right">{row.count}</TableCell>
                            <TableCell className="text-right">{row.percentage}%</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Project Performance Report */}
          <TabsContent value="project">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Summary Cards */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Projects</p>
                      <p className="text-2xl font-bold">{projectReport.length}</p>
                    </div>
                    <Building2 className="h-10 w-10 text-cyan-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Leads</p>
                      <p className="text-2xl font-bold">
                        {projectReport.reduce((sum, row) => sum + (row.total_leads || 0), 0)}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Booked</p>
                      <p className="text-2xl font-bold">
                        {console.log(projectReport)}
{ projectReport.flatMap(p => p.source_breakdown)
  .reduce((sum, s) => sum + Number(s.booked || 0), 0)
}
                      </p>
                    </div>
                    <Trophy className="h-10 w-10 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Avg Conversion</p>
                      <p className="text-2xl font-bold">
                        {projectReport.length > 0 
                          ? (projectReport.reduce((sum, row) => sum + (row.conversion_rate || 0), 0) / projectReport.length).toFixed(1) 
                          : 0}%
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Project Performance Chart */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle>Project Performance Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={projectReport}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="project" 
                      fontSize={10}
                      tick={{ fontSize: 10 }}
                      interval={0}
                      angle={-45}
                      textAnchor="end"
                      height={70}
                    />
                    <YAxis fontSize={12} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="total_leads" fill="#06b6d4" name="Total" />
                    <Bar dataKey="converted" fill="#10b981" name="Booked" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Project Performance Table with Source Breakdown */}
            <Card data-testid="project-report-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Project Performance</CardTitle>
                  <Button
                    variant="outline"
                    onClick={() => exportToCSV(projectReport, 'project_performance')}
                    data-testid="export-project-button"
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Project</TableHead>
                        <TableHead className="text-right">Total Leads</TableHead>
                        <TableHead className="text-right">Booked</TableHead>
                        <TableHead className="text-right">Conversion Rate</TableHead>
                        <TableHead className="text-right">Active Leads</TableHead>
                        <TableHead>Top Sales Rep</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {projectReport.map((row, idx) => (
                        <React.Fragment key={idx}>
                          <TableRow 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => setExpandedProjects(prev => ({...prev, [idx]: !prev[idx]}))}
                          >
                            <TableCell>
                              {row.source_breakdown?.length > 0 && (
                                <span className="text-slate-400">{expandedProjects[idx] ? '▼' : '▶'}</span>
                              )}
                            </TableCell>
                            <TableCell className="font-medium">{row.project}</TableCell>
                            <TableCell className="text-right">{row.total_leads}</TableCell>
                            <TableCell className="text-right">{row.converted}</TableCell>
                            <TableCell className="text-right">
                              <Badge variant={row.conversion_rate >= 10 ? "default" : row.conversion_rate >= 5 ? "secondary" : "outline"}>
                                {row.conversion_rate.toFixed(1)}%
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">{row.active_leads}</TableCell>
                            <TableCell>{row.top_broker}</TableCell>
                          </TableRow>
                          {/* FEATURE 2: Source Breakdown Rows */}
                          {expandedProjects[idx] && row.source_breakdown?.map((src, sIdx) => (
                            <TableRow key={`${idx}-${sIdx}`} className="bg-slate-50">
                              <TableCell></TableCell>
                              <TableCell className="pl-8 text-slate-600">
                                └─ {src.source}
                              </TableCell>
                              <TableCell className="text-right text-slate-600">{src.total_leads}</TableCell>
                              <TableCell className="text-right text-slate-600">{src.booked}</TableCell>
                              <TableCell className="text-right">
                                <Badge variant="outline" className="text-xs">
                                  {src.conversion_rate.toFixed(1)}%
                                </Badge>
                              </TableCell>
                              <TableCell></TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          ))}
                        </React.Fragment>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Site Visits Report */}
          <TabsContent value="site-visits">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Building2 className="h-5 w-5 text-cyan-600" />
                  <span>Site Visits Report</span>
                </CardTitle>
                <Button 
                  onClick={() => exportToCSV(siteVisitsReport, 'site_visits_report')}
                  variant="outline"
                  size="sm"
                  disabled={!siteVisitsReport || siteVisitsReport.length === 0}
                  data-testid="export-site-visits-button"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {siteVisitsReport && siteVisitsReport.length > 0 ? (
                  <>
                    <div className="mb-4 p-4 bg-slate-50 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-sm text-slate-600">Total Leads with Site Visits</p>
                          <p className="text-2xl font-bold text-cyan-600">{siteVisitsReport.length}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Total Site Visits</p>
                          <p className="text-2xl font-bold text-blue-600">
                            {siteVisitsReport.reduce((sum, row) => sum + (row.visit_count || 0), 0)}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Average Visits per Lead</p>
                          <p className="text-2xl font-bold text-green-600">
                            {(siteVisitsReport.reduce((sum, row) => sum + (row.visit_count || 0), 0) / siteVisitsReport.length).toFixed(1)}
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Lead Number</TableHead>
                            <TableHead>Customer Name</TableHead>
                            <TableHead>Mobile</TableHead>
                            <TableHead>Email</TableHead>
                            <TableHead>Projects</TableHead>
                            <TableHead>Assigned To</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead className="text-center">Visit Count</TableHead>
                            <TableHead>First Visit</TableHead>
                            <TableHead>Latest Visit</TableHead>
                            <TableHead>Current Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {siteVisitsReport.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell className="font-medium">{row.lead_number}</TableCell>
                              <TableCell>{row.customer_name}</TableCell>
                              <TableCell>{row.mobile}</TableCell>
                              <TableCell className="text-sm">{row.email || '-'}</TableCell>
                              <TableCell className="text-sm">{row.projects}</TableCell>
                              <TableCell>{row.assigned_to}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="text-xs">
                                  {row.source}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge className="bg-cyan-600">
                                  {row.visit_count}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm">
                                {row.first_visit_date ? format(new Date(row.first_visit_date), 'MMM dd, yyyy') : '-'}
                              </TableCell>
                              <TableCell className="text-sm">
                                {row.latest_visit_date ? format(new Date(row.latest_visit_date), 'MMM dd, yyyy') : '-'}
                              </TableCell>
                              <TableCell>
                                <Badge 
                                  variant={
                                    row.current_status === 'Booked' ? 'default' :
                                    row.current_status === 'Site Visit Done' ? 'secondary' :
                                    'outline'
                                  }
                                  className="text-xs"
                                >
                                  {row.current_status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Building2 className="mx-auto h-12 w-12 text-slate-400 mb-4" />
                    <p className="text-lg text-slate-600 mb-2">No Site Visits Data</p>
                    <p className="text-slate-500">No leads with site visits found for the selected date range.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* FEATURE 3: Bookings Report (replaces Trends) */}
          <TabsContent value="bookings">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Bookings</p>
                      <p className="text-2xl font-bold text-green-600">
                        {bookingsReport?.summary?.total_bookings || 0}
                      </p>
                    </div>
                    <Trophy className="h-10 w-10 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Total Leads</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {bookingsReport?.summary?.total_leads || 0}
                      </p>
                    </div>
                    <Users className="h-10 w-10 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-600">Overall Conversion</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {bookingsReport?.summary?.overall_conversion_rate || 0}%
                      </p>
                    </div>
                    <TrendingUp className="h-10 w-10 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Section 1: Project-wise Bookings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-cyan-600" />
                    Bookings by Project
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {bookingsReport?.by_project?.length > 0 ? (
                      bookingsReport.by_project.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{item.project}</p>
                            <p className="text-xs text-slate-500">{item.total_leads} leads</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{item.total_bookings}</p>
                            <Badge variant="outline" className="text-xs">
                              {item.conversion_rate}%
                            </Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-slate-500 py-4">No bookings data</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Section 2: Source-wise Bookings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="h-5 w-5 text-blue-600" />
                    Bookings by Source
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {bookingsReport?.by_source?.length > 0 ? (
                      bookingsReport.by_source.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{item.source}</p>
                            <p className="text-xs text-slate-500">{item.total_leads} leads</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{item.total_bookings}</p>
                            <Badge variant="outline" className="text-xs">
                              {item.conversion_rate}%
                            </Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-slate-500 py-4">No bookings data</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Section 3: Sales Rep-wise Bookings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Users className="h-5 w-5 text-purple-600" />
                    Bookings by Sales Rep
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {bookingsReport?.by_sales_rep?.length > 0 ? (
                      bookingsReport.by_sales_rep.map((item, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div>
                            <p className="font-medium text-sm">{item.sales_rep}</p>
                            <p className="text-xs text-slate-500">{item.total_leads} leads</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{item.total_bookings}</p>
                            <Badge variant="outline" className="text-xs">
                              {item.conversion_rate}%
                            </Badge>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-center text-slate-500 py-4">No bookings data</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default Reports;